import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';

import { jwtDecode } from 'jwt-decode';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { authService } from '../services/auth.service';
import { authOfflineService } from '../services/auth-offline.service';
import { subscriptionService } from '../services/subscription.service';
import { getTokenPayload } from '../services/storage/localStorage.service';
import { athleteService } from '../services/athlete.service';
import { trainerService } from '../services/trainer.service';
import { gymService } from '../services/gym.service';
import { membershipService } from '../services/membership.service';

import {
  QueryKeys,
  prefetchCommonQueries,
  clearQueryCache,
} from '../config/query-client';

import i18n from '../utils/i18next';
import { offlineStateService } from '../offline/services/offline-state.service.js';
import { offlineApiService } from '../offline/services/offline-api.service.js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    if (import.meta.env.DEV) {
      console.warn(
        'useAuth called without AuthProvider. Ensure AuthProvider wraps the App root in main.jsx.'
      );
      return {
        user: null,
        profile: null,
        isLoggedIn: false,
        loading: true,
        login: async () => ({ success: false }),
        logout: async () => {},
        refreshProfile: async () => {},
        membershipExpired: false,
        membershipInfo: null,
        clearMembershipExpired: () => {},
        offlineAuthState: null,
        offlineUsageAllowed: false,
      };
    }

    throw new Error('useAuth must be used within AuthProvider');
  }

  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);

  const [membershipExpired, setMembershipExpired] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [offlineAuthState, setOfflineAuthState] = useState(
    authOfflineService.getState()
  );
  const [offlineUsageAllowed, setOfflineUsageAllowed] = useState(
    authOfflineService.isOfflineUsageAllowed()
  );

  const queryClient = useQueryClient();

  // Prevent duplicate refresh attempts (React StrictMode double-mounting)
  const hasAttemptedRefresh = useRef(false);
  const reconnectingRef = useRef(false);

  // Use TanStack Query for profile data based on user role

  const { data: athleteProfile } = useQuery({
    queryKey: QueryKeys.PROFILE(user?.athleteId),

    queryFn: () => athleteService.getAthleteById(user.athleteId),

    enabled: !!user && user.role === 'athlete' && !!user.athleteId,

    staleTime: 5 * 60 * 1000,
  });

  const { data: trainerProfile } = useQuery({
    queryKey: QueryKeys.PROFILE(user?.trainerId),

    queryFn: () => trainerService.getTrainerById(user.trainerId),

    enabled: !!user && user.role === 'trainer' && !!user.trainerId,

    staleTime: 5 * 60 * 1000,
  });

  const { data: gymProfile } = useQuery({
    queryKey: QueryKeys.GYM_ME,

    queryFn: () => gymService.getMe(),

    enabled: !!user && user.role === 'gym',

    staleTime: 5 * 60 * 1000,
  });

  // Compute profile based on user role

  const profile = React.useMemo(() => {
    if (!user) return null;

    if (user.role === 'athlete' && athleteProfile) {
      const profileData = athleteProfile.athlete || athleteProfile;

      if (athleteProfile.user)
        profileData.username = athleteProfile.user.username;

      return profileData;
    }

    if (user.role === 'trainer' && trainerProfile) {
      const profileData = trainerProfile.trainer || trainerProfile;

      if (trainerProfile.user)
        profileData.username = trainerProfile.user.username;

      return profileData;
    }

    if (user.role === 'gym' && gymProfile) {
      return gymProfile.profile;
    }

    return null;
  }, [user, athleteProfile, trainerProfile, gymProfile]);

  // Refresh profile function - now uses query invalidation
  const refreshProfile = useCallback(async () => {
    if (!user) return;

    if (user.role === 'athlete' && user.athleteId) {
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.PROFILE(user.athleteId),
      });
    } else if (user.role === 'trainer' && user.trainerId) {
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.PROFILE(user.trainerId),
      });
    } else if (user.role === 'gym') {
      await queryClient.invalidateQueries({ queryKey: QueryKeys.GYM_ME });
    }
  }, [user, queryClient]);

  const syncOfflineAuthState = useCallback(() => {
    const state = authOfflineService.getState();
    setOfflineAuthState(state);
    setOfflineUsageAllowed(authOfflineService.isOfflineUsageAllowed(state));
  }, []);

  const notifyOfflineGraceWarning = useCallback(() => {
    if (
      !offlineStateService.isCurrentlyOnline() &&
      authOfflineService.shouldWarn() &&
      !authOfflineService.hasWarned()
    ) {
      toast(
        'Offline grace will expire soon. Reconnect to extend access and validate subscription.',
        {
          icon: '⚠️',
          duration: 8000,
        }
      );
      authOfflineService.markWarned();
      syncOfflineAuthState();
    }
  }, [syncOfflineAuthState]);

  const validateGymSubscription = useCallback(
    async (userData) => {
      if (!userData || userData.role !== 'gym' || !userData.gymId) {
        return true;
      }

      try {
        const membershipStatus = await subscriptionService.validateGymSubscription();

        if (membershipStatus.expired || !membershipStatus.valid) {
          setMembershipExpired(true);
          setMembershipInfo(membershipStatus);
          localStorage.removeItem('accessToken');
          setUser(null);
          return false;
        }

        setMembershipExpired(false);
        setMembershipInfo(membershipStatus);
        authOfflineService.markSubscriptionValidated();
        syncOfflineAuthState();
        return true;
      } catch (error) {
        // If we're offline or the failure is due to network, allow offline usage.
        if (
          !offlineStateService.isCurrentlyOnline() ||
          error.offlineRefresh ||
          error.message?.includes('Offline')
        ) {
          return true;
        }

        // If server explicitly says membership expired, enforce logout.
        if (
          error.response?.status === 403 ||
          error.response?.data?.code === 'MEMBERSHIP_EXPIRED'
        ) {
          // If grace is still valid, keep the local session but warn the user.
          if (authOfflineService.isOfflineGraceValid()) {
            notifyOfflineGraceWarning();
            return true;
          }

          setMembershipExpired(true);
          setMembershipInfo(error.response?.data || null);
          localStorage.removeItem('accessToken');
          setUser(null);
          return false;
        }

        // For other unexpected errors, don't log the user out while grace is valid.
        if (authOfflineService.isOfflineGraceValid()) {
          console.warn('Membership check transient failure during grace period:', error);
          notifyOfflineGraceWarning();
          return true;
        }

        console.error('Membership check failed:', error);
        return true;
      }
    },
    [syncOfflineAuthState]
  );

  // Initialize user from token on mount

  useEffect(() => {
    const initAuth = async () => {
      offlineStateService.syncFromNavigator();
      const tokenPayload = getTokenPayload();
      const tokenUser = getUserFromToken();
      const offlineGraceValid = authOfflineService.isOfflineGraceValid();

      const isOnline = offlineStateService.isCurrentlyOnline();

      if (tokenUser) {
        setUser(tokenUser);
        syncOfflineAuthState();

        if (isOnline) {
          const valid = await validateGymSubscription(tokenUser);
          if (valid && tokenUser.role !== 'gym') {
            authOfflineService.markSubscriptionValidated();
            syncOfflineAuthState();
          }
        }

        notifyOfflineGraceWarning();
        setLoading(false);
        return;
      }

      if (tokenPayload && offlineGraceValid) {
        setUser(tokenPayload);
        syncOfflineAuthState();

        if (isOnline) {
          try {
            const refreshResponse = await authService.refresh();
            if (!refreshResponse?.queued && refreshResponse?.accessToken) {
              localStorage.setItem('accessToken', refreshResponse.accessToken);
              const refreshedUser = getUserFromToken();
              if (refreshedUser) {
                setUser(refreshedUser);
                authOfflineService.markAuthSuccess();
                const valid = await validateGymSubscription(refreshedUser);
                if (valid && refreshedUser.role !== 'gym') {
                  authOfflineService.markSubscriptionValidated();
                  syncOfflineAuthState();
                }
              }
            }
          } catch (error) {
            if (!error.offlineRefresh) {
              console.error('Token refresh failed during grace fallback:', error);
            }
          }
        }

        notifyOfflineGraceWarning();
        setLoading(false);
        return;
      }

      // No token at all — nothing to refresh, skip network call
      if (!tokenPayload) {
        setLoading(false);
        return;
      }

      if (!hasAttemptedRefresh.current && isOnline) {
        hasAttemptedRefresh.current = true;

        try {
          const refreshResponse = await authService.refresh();

          if (!refreshResponse?.queued && refreshResponse?.accessToken) {
            localStorage.setItem('accessToken', refreshResponse.accessToken);
            const refreshedUser = getUserFromToken();

            if (refreshedUser) {
              setUser(refreshedUser);
              authOfflineService.markAuthSuccess();
              const valid = await validateGymSubscription(refreshedUser);
              if (valid && refreshedUser.role !== 'gym') {
                authOfflineService.markSubscriptionValidated();
                syncOfflineAuthState();
              }
            }
          }
        } catch (error) {
          if (
            error.offlineRefresh &&
            offlineGraceValid &&
            tokenPayload
          ) {
            setUser(tokenPayload);
            syncOfflineAuthState();
            notifyOfflineGraceWarning();
          } else {
            console.error('Token refresh failed:', error);
          }
        } finally {
          setLoading(false);
        }

        return;
      }

      setLoading(false);
      return;
    };

    initAuth();
  }, []);

  useEffect(() => {
    const handleReconnect = async () => {
      if (reconnectingRef.current || !user) return;
      reconnectingRef.current = true;

      try {
        const refreshResponse = await authService.refresh();

        if (!refreshResponse?.queued && refreshResponse?.accessToken) {
          localStorage.setItem('accessToken', refreshResponse.accessToken);
          const refreshedUser = getUserFromToken();

          if (refreshedUser) {
            setUser(refreshedUser);
            authOfflineService.markAuthSuccess();
            const valid = await validateGymSubscription(refreshedUser);
            if (valid && refreshedUser.role !== 'gym') {
              authOfflineService.markSubscriptionValidated();
              syncOfflineAuthState();
            }
          }
        }
      } catch (error) {
        if (error.offlineRefresh) {
          notifyOfflineGraceWarning();
          return;
        }

        if (error.status === 401 || error.status === 403) {
          if (!authOfflineService.isOfflineGraceValid()) {
            setUser(null);
            setMembershipExpired(false);
            setMembershipInfo(null);
            authOfflineService.clearState();
            return;
          }

          notifyOfflineGraceWarning();
          return;
        }

        if (!authOfflineService.isOfflineGraceValid()) {
          setUser(null);
        }
      } finally {
        reconnectingRef.current = false;
      }
    };

    const unsubscribe = offlineStateService.subscribe((state) => {
      if (state.isOnline) {
        handleReconnect().catch(console.error);
      } else {
        notifyOfflineGraceWarning();
      }
    });

    return () => unsubscribe();
  }, [user, validateGymSubscription, syncOfflineAuthState, notifyOfflineGraceWarning]);

  // Async login logic using authService
  const login = useCallback(async (username, password) => {
    try {
      offlineStateService.syncFromNavigator();
      const response = await authService.login(username, password);

      if (response?.queued) {
        return {
          success: false,
          error: i18n.t('errors.networkError'),
        };
      }

      if (response?.accessToken) {
        // Save token first so membership check can use it
        localStorage.setItem('accessToken', response.accessToken);

        // For gym users, check membership before completing login
        if (response.role === 'gym') {
          try {
            const membershipStatus = await membershipService.getStatus();
            if (membershipStatus.expired || !membershipStatus.valid) {
              setMembershipExpired(true);
              setMembershipInfo(membershipStatus);
              return {
                success: false,
                membershipExpired: true,
                error: i18n.t('login.messages.membershipExpired'),
              };
            }
            setMembershipExpired(false);
            setMembershipInfo(membershipStatus);
            authOfflineService.markSubscriptionValidated();
            syncOfflineAuthState();
          } catch (error) {
            if (
              error.response?.status === 403 ||
              error.response?.data?.code === 'MEMBERSHIP_EXPIRED'
            ) {
              setMembershipExpired(true);
              setMembershipInfo(error.response?.data);
              return {
                success: false,
                membershipExpired: true,
                error:
                  error.response?.data?.message ||
                  i18n.t('login.messages.membershipExpired'),
              };
            }
            console.error('Membership check failed during login:', error);
          }
        }

        const userData = getUserFromToken();

        if (response.role === 'gym') {
          authOfflineService.markAuthSuccess();
        } else {
          authOfflineService.markAuthSuccess();
          authOfflineService.markSubscriptionValidated();
          syncOfflineAuthState();
        }

        setUser(userData);
        sessionStorage.setItem('postLoginSync', 'true');

        // Fetch and apply user's language preference from database
        try {
          const langResponse = await authService.getUserLanguage();
          if (langResponse?.language) {
            localStorage.setItem('app-language', langResponse.language);
            await i18n.changeLanguage(langResponse.language);
          }
        } catch (error) {
          console.error('Failed to load user language:', error);
        }

        // Prefetch commonly used queries after login (non-blocking)

        prefetchCommonQueries(userData).catch(console.error);

        return { success: true, role: response.role || null };
      } else {
        return {
          success: false,
          error: i18n.t('login.messages.noTokenReceived'),
        };
      }
    } catch (error) {
      // baseApi throws { status, message, code, ... } directly
      let errorMessage =
        error.message ||
        error.error ||
        (typeof error === 'string'
          ? error
          : i18n.t('login.messages.loginFailed'));

      // Handle specific backend error messages
      if (
        errorMessage.includes('Refresh token required') ||
        errorMessage.includes('refresh token') ||
        errorMessage.includes('Invalid credentials')
      ) {
        errorMessage = i18n.t('login.messages.error');
      }

      return { success: false, error: errorMessage };
    }
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore logout errors
    }

    localStorage.removeItem('accessToken');
    authOfflineService.clearState();
    syncOfflineAuthState();

    // Clear all TanStack Query cache and offline API cache on logout

    clearQueryCache();
    await offlineApiService.clearAllCaches();

    setUser(null);
    setMembershipExpired(false);
    setMembershipInfo(null);
  }, [syncOfflineAuthState]);

  const clearMembershipExpired = () => {
    setMembershipExpired(false);
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      user,
      profile,
      isLoggedIn: !!user,
      loading,
      login,
      logout,
      refreshProfile,
      membershipExpired,
      membershipInfo,
      clearMembershipExpired,
      offlineAuthState,
      offlineUsageAllowed,
    }),
    [
      user,
      profile,
      loading,
      login,
      logout,
      refreshProfile,
      membershipExpired,
      membershipInfo,
      offlineAuthState,
      offlineUsageAllowed,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// move non-component function to bottom or separate file

export const getUserFromToken = () => {
  const token = localStorage.getItem('accessToken');

  if (!token) return null;

  try {
    const decoded = jwtDecode(token);

    if (decoded.exp * 1000 < Date.now()) {
      if (authOfflineService.isOfflineGraceValid()) {
        return decoded;
      }

      localStorage.removeItem('accessToken');
      return null;
    }

    return decoded;
  } catch {
    localStorage.removeItem('accessToken');

    return null;
  }
};
